import type { ClinicalEntry, EntryCategory, Visit, VisitReview } from '../types/clinicalDemo'

export const clinicalPatient = {
  id: 'PAT-DEMO-01',
  name: 'Morgan Lee',
  description: 'Fictional adult patient · sleep disruption during changing work schedules',
}

export const clinicalScopeNote =
  'This example illustrates documentation, not diagnosis. No diagnostic AI, treatment recommendations, biomarker interpretation or NCJI scoring are included. Clinical review does not require civic judgment assessment.'

export const documentationOnlyLabel = 'This reviews documentation. It does not establish a diagnosis.'

export const categoryInfo: Record<EntryCategory, { label: string; description: string }> = {
  'patient-report': { label: 'Patient report', description: 'What the patient said, in their account.' },
  measurement: { label: 'Recorded measurement', description: 'A recorded value, with how it was obtained.' },
  observation: { label: 'Clinician observation', description: 'What the clinician directly observed.' },
  interpretation: { label: 'Interpretation', description: 'A professional reading of the information so far.' },
}

export const visits: Visit[] = [
  {
    id: 'VIS-01',
    label: 'Visit 1 · Initial consultation',
    date: '2026-08-04',
    context: 'Morgan describes difficulty sleeping since moving to rotating shifts.',
  },
  {
    id: 'VIS-02',
    label: 'Visit 2 · Follow-up',
    date: '2026-08-25',
    context: 'Morgan returns with a two-week sleep diary.',
  },
]

export const workflowChecklist = [
  'Identify whether an entry is a report, measurement, observation or interpretation.',
  'Record the visit, author role and time.',
  'State missing context and uncertainty.',
  'Preserve reviewed records and append later corrections.',
]

export const checklistLabel = 'Synthetic workflow checklist — not a clinical guideline or medical reference.'

const v1 = '2026-08-04T09:30:00.000Z'
const v2 = '2026-08-25T10:15:00.000Z'

export const seedEntries: ClinicalEntry[] = [
  {
    id: 'ENT-001',
    visitId: 'VIS-01',
    category: 'patient-report',
    text: 'Reports difficulty staying asleep since moving to rotating shifts about six weeks ago. Unsure how many nights per week are affected.',
    authorRole: 'Clinician',
    at: v1,
    isAddendum: false,
  },
  {
    id: 'ENT-002',
    visitId: 'VIS-01',
    category: 'measurement',
    text: 'Blood pressure 124/78 mmHg, measured seated at intake.',
    authorRole: 'Clinician',
    at: v1,
    isAddendum: false,
  },
  {
    id: 'ENT-003',
    visitId: 'VIS-01',
    category: 'observation',
    text: 'Alert and engaged throughout the conversation; yawned several times.',
    authorRole: 'Clinician',
    at: v1,
    isAddendum: false,
  },
  {
    id: 'ENT-004',
    visitId: 'VIS-01',
    category: 'interpretation',
    text: 'Account is incomplete: timing of sleep disruption relative to shift changes is not yet known. Sleep diary agreed before any assessment.',
    authorRole: 'Clinician',
    at: v1,
    isAddendum: false,
  },
  {
    id: 'ENT-005',
    visitId: 'VIS-02',
    category: 'patient-report',
    text: 'Reports shorter sleep after night shifts and feeling unrefreshed on those days.',
    authorRole: 'Clinician',
    at: v2,
    isAddendum: false,
  },
  {
    id: 'ENT-006',
    visitId: 'VIS-02',
    category: 'measurement',
    text: 'Self-recorded sleep diary, 14 days: average 5.4 hours on days after night shifts; 7.1 hours on other days.',
    authorRole: 'Clinician',
    at: v2,
    isAddendum: false,
  },
]

export const seedReviews: VisitReview[] = [
  {
    id: 'CRV-001',
    visitId: 'VIS-01',
    outcome: 'reviewed',
    explanation:
      'Categories are distinct and the interpretation states what is still unknown. Documentation is complete for an initial consultation.',
    entryIds: ['ENT-001', 'ENT-002', 'ENT-003', 'ENT-004'],
    reviewerRole: 'Authorized reviewer',
    at: '2026-08-05T15:00:00.000Z',
  },
]
